import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Table,
  Tag,
  Spin,
  message,
  Tabs,
  Modal,
  Badge,
  Empty,
  Progress,
  DatePicker,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  CalendarOutlined,
  BarChartOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "api/axiosInstance";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DepartmentAttendanceService from "../../../Services/AdminServices/DepAttendnce";
import useAttendance from "../../../Hooks/AdminHooks/useAttendanceReport";
import {
  AttendanceStatusEnum,
  STATUS_CONFIG,
  AttendenceStatusLabel,
} from "../../../Constants/Enums/Attendance/AttendanceStatusEnum";
import {
  WorkLifeBalanceEnum,
  WorkLifeBalanceLabel,
} from "../../../Constants/Enums/Attendance/WorkLifeBalanceEnum";
import API from "api/endpoints";
const { Option } = Select;

const DepAttendance = () => {
  const service = DepartmentAttendanceService();
  const {
    attendanceData,
    loading,
    selectedDepartment,
    selectedEmployee,
    currentDate,
    setSelectedDepartment,
    setCurrentDate,
    setSelectedEmployee,
    getAttendanceByDate,
    getTodayEmployees,
    handleEmployeeSelect,
    loadDepartmentAttendance,
  } = useAttendance(service);

  const [activeTab, setActiveTab] = useState("today");
  const [calendarView, setCalendarView] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [workLifeBalanceData, setWorkLifeBalanceData] = useState([]);
  const [loadingWorkLife, setLoadingWorkLife] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [employeeSummaryModalVisible, setEmployeeSummaryModalVisible] = useState(false);
  const [selectedEmployeeForSummary, setSelectedEmployeeForSummary] = useState(null);
  const [employeeSummaryData, setEmployeeSummaryData] = useState(null);
  const [loadingEmployeeSummary, setLoadingEmployeeSummary] = useState(false);
  const [workTimingSettings, setWorkTimingSettings] = useState({
    startTime: "09:00",
    endTime: "18:00",
    graceMinutes: 15,
    halfDayThresholdHours: 4,
    fullDayRequiredHours: 8,
    effectiveDate: dayjs().format("YYYY-MM-DD"),
  });
  const [isEditingTimings, setIsEditingTimings] = useState(false);
  const [loadingTimings, setLoadingTimings] = useState(false);
  const [viewTypeFilter, setViewTypeFilter] = useState(0); // 0 = week, 1 = month

  // Add this to see the actual API response
  const formatToAMPM = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const adjustedHours = hours % 12 || 12;
    return `${adjustedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  useEffect(() => {
    const loadWorkTimingSettings = async () => {
      try {
        setLoadingTimings(true);
        const settings = await service.getActiveWorkingHours();

        if (settings) {
          const formattedSettings = {
            ...settings,
            startTime: settings.startTime ? settings.startTime.substring(0, 5) : "09:00",
            endTime: settings.endTime ? settings.endTime.substring(0, 5) : "18:00",
            startTimeAMPM: formatToAMPM(settings.startTime.substring(0, 5)),
            endTimeAMPM: formatToAMPM(settings.endTime.substring(0, 5)),
            effectiveDate: settings.effectiveDate
              ? dayjs(settings.effectiveDate).format("YYYY-MM-DD")
              : dayjs().format("YYYY-MM-DD"),
          };
          setWorkTimingSettings(formattedSettings);

          setWorkTimingSettings(formattedSettings);
        }
      } catch (error) {
        console.error("Error loading work timing settings:", error);
        message.error("Failed to load work timing settings");
      } finally {
        setLoadingTimings(false);
      }
    };

    loadWorkTimingSettings();
  }, []);

  // Save work timing settings
  const saveWorkTimingSettings = async () => {
    try {
      setLoadingTimings(true);

      // Prepare data for API
      const apiData = {
        startTime: workTimingSettings.startTime + ":00", // Add seconds for API
        endTime: workTimingSettings.endTime + ":00", // Add seconds for API
        graceMinutes: workTimingSettings.graceMinutes,
        halfDayThresholdHours: workTimingSettings.halfDayThresholdHours,
        fullDayRequiredHours: workTimingSettings.fullDayRequiredHours,
        effectiveDate: dayjs(workTimingSettings.effectiveDate).toISOString(),
      };

      await service.addWorkingHours(apiData);

      const updatedSettings = await service.getActiveWorkingHours();
      if (updatedSettings) {
        const formattedSettings = {
          ...updatedSettings,
          startTime: updatedSettings.startTime
            ? updatedSettings.startTime.substring(0, 5)
            : "09:00",
          endTime: updatedSettings.endTime ? updatedSettings.endTime.substring(0, 5) : "18:00",
          startTimeAMPM: formatToAMPM(updatedSettings.startTime.substring(0, 5)),
          endTimeAMPM: formatToAMPM(updatedSettings.endTime.substring(0, 5)),
          effectiveDate: updatedSettings.effectiveDate
            ? dayjs(updatedSettings.effectiveDate).format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD"),
        };
        setWorkTimingSettings(formattedSettings);
      }

      message.success("Work timing settings updated successfully!");
      setIsEditingTimings(false);
    } catch (error) {
      console.error("Error saving work timing settings:", error);
      message.error("Failed to update work timing settings");
    } finally {
      setLoadingTimings(false);
    }
  };

  // Fetch individual employee attendance data
  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeAttendance();
    }
  }, [selectedEmployee, currentDate, calendarView]);

  // Fetch Work Life Balance data when department changes
  useEffect(() => {
    if (selectedDepartment && activeTab === "worklife") {
      fetchWorkLifeBalanceData();
    }
  }, [selectedDepartment, activeTab, currentDate]);

  const fetchEmployeeAttendance = async () => {
    try {
      setLoadingEmployee(true);

      let startDate, endDate;

      if (calendarView === "month") {
        startDate = dayjs(currentDate).startOf("month").format("YYYY-MM-DD");
        endDate = dayjs(currentDate).endOf("month").format("YYYY-MM-DD");
      } else if (calendarView === "week") {
        startDate = dayjs(currentDate).startOf("week").format("YYYY-MM-DD");
        endDate = dayjs(currentDate).endOf("week").format("YYYY-MM-DD");
      }

      const viewType = calendarView === "month" ? 1 : 0;

      const response = await axiosInstance.get(
        API.attendance.userAttendance(selectedEmployee.id, viewType, startDate, endDate)
      );

      setEmployeeRecords(response.data?.records || []);
    } catch (error) {
      console.error("Error fetching employee attendance:", error);
      message.error("Failed to load employee attendance data");
    } finally {
      setLoadingEmployee(false);
    }
  };

  const fetchWorkLifeBalanceData = async () => {
    try {
      if (!selectedDepartment) {
        message.warning("Please select a department first");
        return;
      }

      setLoadingWorkLife(true);

      //  Ensure month/year are valid numbers (fallback to current)
      const month = selectedMonth || new Date().getMonth() + 1;
      const year = selectedYear || new Date().getFullYear();

      //  Call correct service
      const response = await service.getWorkLifeBalanceReport(selectedDepartment, month, year);

      //  Log full response structure

      //  Safely extract employees array
      const employees = Array.isArray(response?.employees)
        ? response.employees
        : Array.isArray(response)
        ? response
        : [];

      if (employees.length === 0) {
        message.info("No work-life balance data available for this period.");
      }

      //  Update state
      setWorkLifeBalanceData(employees);
    } catch (error) {
      console.error("❌ Error fetching Work-Life Balance data:", error);
      message.error("Failed to load work-life balance data");
      setWorkLifeBalanceData([]);
    } finally {
      setLoadingWorkLife(false);
    }
  };

  const refreshTodayData = () => {
    if (selectedDepartment) {
      // Calculate date range based on view type
      let startDate, endDate;

      if (viewTypeFilter === 0) {
        // Week view
        const today = dayjs();
        startDate = today.startOf("week").format("YYYY-MM-DD");
        endDate = today.endOf("week").format("YYYY-MM-DD");
      } else {
        // Month view
        const today = dayjs();
        startDate = today.startOf("month").format("YYYY-MM-DD");
        endDate = today.endOf("month").format("YYYY-MM-DD");
      }

      // Pass the date range along with viewType
      loadDepartmentAttendance(selectedDepartment, viewTypeFilter, startDate, endDate);
      message.info("Refreshing attendance data...");
    } else {
      message.warning("Please select a department first");
    }
  };

  // Fetch individual employee summary data
  const fetchEmployeeSummary = async (employee) => {
    try {
      setLoadingEmployeeSummary(true);
      setSelectedEmployeeForSummary(employee);
      const response = await axiosInstance.get(
        API.workLifeBalance.getEmployeeReport(employee.employeeId, selectedMonth, selectedYear)
      );

      setEmployeeSummaryData(response.data);
      setEmployeeSummaryModalVisible(true);
    } catch (error) {
      console.error("Error fetching employee summary:", error);
      message.error("Failed to load employee summary data");
    } finally {
      setLoadingEmployeeSummary(false);
    }
  };

  // Handle employee card click
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setActiveTab("calendar");
    message.info(`Viewing ${employee.name}'s attendance details`);
  };

  // Calendar navigation
  const goToPrevious = () => {
    if (calendarView === "month") {
      setCurrentDate((prev) => dayjs(prev).subtract(1, "month").toDate());
    } else if (calendarView === "week") {
      setCurrentDate((prev) => dayjs(prev).subtract(1, "week").toDate());
    }
  };

  const goToNext = () => {
    if (calendarView === "month") {
      setCurrentDate((prev) => dayjs(prev).add(1, "month").toDate());
    } else if (calendarView === "week") {
      setCurrentDate((prev) => dayjs(prev).add(1, "week").toDate());
    }
  };

  useEffect(() => {
    if (selectedDepartment) {
      // Calculate date range based on view type
      let startDate, endDate;

      if (viewTypeFilter === 0) {
        // Week view
        const today = dayjs();
        startDate = today.startOf("week").format("YYYY-MM-DD");
        endDate = today.endOf("week").format("YYYY-MM-DD");
      } else {
        // Month view
        const today = dayjs();
        startDate = today.startOf("month").format("YYYY-MM-DD");
        endDate = today.endOf("month").format("YYYY-MM-DD");
      }

      // Pass the date range along with viewType
      loadDepartmentAttendance(selectedDepartment, viewTypeFilter, startDate, endDate);
    }
  }, [viewTypeFilter, selectedDepartment]); // Add selectedDepartment to dependencies

  const goToToday = () => {
    setCurrentDate(new Date());
    setActiveTab("today");
  };

  // Generate calendar days for department view
  const generateCalendarDays = () => {
    if (calendarView === "month") {
      const firstDay = dayjs(currentDate).startOf("month");
      const lastDay = dayjs(currentDate).endOf("month");
      const firstDayOfWeek = firstDay.day();

      const days = [];

      // Previous month days
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = firstDay.subtract(i + 1, "day");
        days.push({ date: date.toDate(), isOtherMonth: true });
      }

      // Current month days
      for (let i = 0; i < lastDay.date(); i++) {
        const date = firstDay.add(i, "day");
        days.push({ date: date.toDate(), isOtherMonth: false });
      }

      // Next month days
      const totalCells = 42;
      const nextMonthDays = totalCells - days.length;
      for (let i = 1; i <= nextMonthDays; i++) {
        const date = lastDay.add(i, "day");
        days.push({ date: date.toDate(), isOtherMonth: true });
      }

      return days;
    } else {
      const startOfWeek = dayjs(currentDate).startOf("week");
      const days = [];

      for (let i = 0; i < 7; i++) {
        const date = startOfWeek.add(i, "day");
        days.push({ date: date.toDate(), isOtherMonth: false });
      }

      return days;
    }
  };

  // Helper function to convert hex to rgb
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : "0, 0, 0";
  };

  // Get department attendance for specific date
  const getDepartmentAttendanceForDate = (date) => {
    try {
      const attendance = getAttendanceByDate(date);
      return (
        attendance || {
          totalPresent: 0,
          totalAbsent: 0,
          totalOnLeave: 0,
          totalLate: 0,
          isWeekend: false,
          isHoliday: false,
          users: [],
        }
      );
    } catch (error) {
      console.error("Error getting department attendance for date:", error);
      return {
        totalPresent: 0,
        totalAbsent: 0,
        totalOnLeave: 0,
        totalLate: 0,
        isWeekend: false,
        isHoliday: false,
        users: [],
      };
    }
  };

  // Format time function for individual employee calendar
  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "";

    const parts = timeString.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;

    return `${hours}h ${minutes}m`;
  };

  // Get attendance record for individual employee
  const getEmployeeAttendanceRecord = (date) => {
    const dateString = dayjs(date).format("YYYY-MM-DD");
    return employeeRecords.find((record) => record.date === dateString);
  };

  // Format worked hours for table
  const formatWorkedHours = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "0h";
    try {
      const [hours, minutes] = timeString.split(":");
      const h = parseInt(hours),
        m = parseInt(minutes);
      if (h === 0 && m === 0) return "0h";
      if (h === 0) return `${m}m`;
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    } catch {
      return "0h";
    }
  };

  // Get status text from status code
  const getStatusText = (statusCode) => {
    return AttendenceStatusLabel[statusCode] || "Unknown";
  };

  // In DepAttendance.js

  const getFilteredEmployees = () => {
    let employees = getTodayEmployees();

    // Filter by search term
    if (searchTerm) {
      employees = employees.filter((employee) =>
        employee.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by department - using ID instead of name
    if (departmentFilter) {
      // Find the selected department to get its name
      const selectedDept = attendanceData.departments.find((dept) => dept.id === departmentFilter);

      if (selectedDept) {
        employees = employees.filter(
          (employee) => employee.department?.toLowerCase() === selectedDept.name.toLowerCase()
        );
      }
    }

    // Filter by status - handle undefined
    if (statusFilter !== undefined && statusFilter !== null) {
      employees = employees.filter(
        (employee) => employee.statusCode?.toString() === statusFilter.toString()
      );
    }

    return employees;
  };

  // Get Work Life Balance Status using the  enums
  const getWorkLifeBalanceStatus = (differencePercent) => {
    // Determine status based on difference percentage
    if (differencePercent >= -5 && differencePercent <= 5) {
      return {
        status: WorkLifeBalanceLabel[WorkLifeBalanceEnum.Excellent],
        statusEnum: WorkLifeBalanceEnum.Excellent,
        color: "#4CAF50", // Green
      };
    } else if (differencePercent >= -10 && differencePercent <= 10) {
      return {
        status: WorkLifeBalanceLabel[WorkLifeBalanceEnum.Acceptable],
        statusEnum: WorkLifeBalanceEnum.Acceptable,
        color: "#8BC34A", // Light Green
      };
    } else if (differencePercent < -10) {
      return {
        status: WorkLifeBalanceLabel[WorkLifeBalanceEnum.Poor],
        statusEnum: WorkLifeBalanceEnum.Poor,
        color: "#FF9800", // Orange
      };
    } else {
      return {
        status: WorkLifeBalanceLabel[WorkLifeBalanceEnum.Overworked],
        statusEnum: WorkLifeBalanceEnum.Overworked,
        color: "#F44336", // Red
      };
    }
  };

  // Render individual employee calendar
  const renderIndividualEmployeeCalendar = () => {
    const calendarDays = generateCalendarDays();
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <>
        {/* Week Days Header */}
        <div className="calendar-weekdays">
          {weekDays.map((day) => (
            <div key={day} className="calendar-weekday">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <Spin spinning={loadingEmployee}>
          <div className={`calendar-grid ${calendarView}-view`}>
            {calendarDays.map(({ date, isOtherMonth }) => {
              const record = getEmployeeAttendanceRecord(date);
              const isToday = dayjs(date).isSame(dayjs(), "day");
              const isFuture = dayjs(date).isAfter(dayjs(), "day");
              const isHovered = hoveredDate === date.toISOString();
              const status = record ? STATUS_CONFIG[record.status] : null;

              return (
                <div
                  key={date.toISOString()}
                  className={`calendar-day ${isOtherMonth ? "other-month" : ""} ${
                    isToday ? "today" : ""
                  } ${isFuture ? "future-date" : ""}`}
                  onMouseEnter={() => !isFuture && setHoveredDate(date.toISOString())}
                  onMouseLeave={() => setHoveredDate(null)}
                >
                  {/* Future Date Overlay */}
                  {isFuture && (
                    <div className="future-date-overlay">
                      <i className="fas fa-lock"></i>
                      <span>Future Date</span>
                    </div>
                  )}

                  {/* Day Header */}
                  <div className="day-header">
                    <div className={`day-number ${isToday ? "today" : ""}`}>{date.getDate()}</div>
                    {status && !isOtherMonth && !isFuture && (
                      <div
                        className={`day-status ${status.class}`}
                        style={{
                          backgroundColor: status.backgroundColor,
                          color: status.color,
                          border: `1px solid rgba(${hexToRgb(status.color)}, 0.2)`,
                        }}
                      >
                        {status.short}
                      </div>
                    )}
                  </div>

                  {/* Day Content */}
                  {!isOtherMonth && !isFuture && (
                    <div className="day-content">
                      {record &&
                        record.totalWorkedHours &&
                        record.totalWorkedHours !== "00:00:00" && (
                          <div className="day-hours">
                            <i className="fas fa-clock"></i>
                            {formatTime(record.totalWorkedHours)}
                          </div>
                        )}

                      {record && record.overtimeHours && record.overtimeHours !== "00:00:00" && (
                        <div className="day-overtime">
                          <i className="fas fa-plus-circle"></i>
                          {formatTime(record.overtimeHours)}
                        </div>
                      )}

                      {/* Indicators */}
                      {(record?.isLate || record?.isEarlyLeave) && (
                        <div className="day-indicators">
                          {record.isLate && (
                            <div className="indicator indicator-late">
                              <i className="fas fa-clock"></i> Late
                            </div>
                          )}
                          {record.isEarlyLeave && (
                            <div className="indicator indicator-early">
                              <i className="fas fa-running"></i> Early
                            </div>
                          )}
                        </div>
                      )}

                      {/* No Data */}
                      {!record && (
                        <div className="day-hours">
                          <i className="fas fa-database"></i>
                          No Data
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hover Details */}
                  {isHovered && !isFuture && (
                    <div className="day-details">
                      <h6>{dayjs(date).format("ddd, MMM D, YYYY")}</h6>
                      {status ? (
                        <>
                          <p>
                            <strong>Status:</strong> {status.name}
                          </p>
                          {record &&
                            record.totalWorkedHours &&
                            record.totalWorkedHours !== "00:00:00" && (
                              <p>
                                <strong>Worked:</strong> {formatTime(record.totalWorkedHours)}
                              </p>
                            )}
                          {record &&
                            record.overtimeHours &&
                            record.overtimeHours !== "00:00:00" && (
                              <p>
                                <strong>Overtime:</strong> {formatTime(record.overtimeHours)}
                              </p>
                            )}
                          {record?.firstCheckIn && (
                            <p>
                              <strong>First In:</strong>{" "}
                              {dayjs(record.firstCheckIn).format("HH:mm")}
                            </p>
                          )}
                          {record?.lastCheckOut && (
                            <p>
                              <strong>Last Out:</strong>{" "}
                              {dayjs(record.lastCheckOut).format("HH:mm")}
                            </p>
                          )}
                          {record?.remarks && (
                            <p>
                              <strong>Remarks:</strong> {record.remarks}
                            </p>
                          )}
                        </>
                      ) : (
                        <p>
                          <strong>Status:</strong> No attendance data
                        </p>
                      )}
                      <div className="click-hint">Hover for details</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Spin>

        {/* Status Legend */}
        <div className="status-legend">
          <div className="legend-title">Employee Status Legend:</div>
          <div className="legend-items">
            {Object.entries(STATUS_CONFIG).map(([status, config]) => (
              <div key={status} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: config.color }} />
                <span>{config.name}</span>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  // Render employee table for today's attendance
  const renderEmployeeTable = () => {
    if (loading.department) {
      return (
        <div className="text-center py-4">
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>Loading attendance data...</div>
        </div>
      );
    }

    const employees = getFilteredEmployees();

    if (!employees || employees.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <div>No attendance data available for the selected period</div>
              <div style={{ fontSize: "12px", color: "#999", marginTop: "8px" }}>
                Try selecting a different department or changing the view type
              </div>
              <Button
                className="btn-primary"
                onClick={refreshTodayData}
                style={{ marginTop: "16px" }}
              >
                Refresh Data
              </Button>
            </div>
          }
        />
      );
    }

    // Table columns - FIX: Ensure department column shows data
    const columns = [
      {
        title: "Employee",
        dataIndex: "name",
        key: "name",
        render: (name, record) => (
          <div
            className="employee-info-cell"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <div className="employee-avatar-small">
              <UserOutlined style={{ fontSize: "16px", color: "#666" }} />
            </div>
            <div className="employee-details">
              <div className="employee-name" style={{ fontWeight: "500" }}>
                {name}
              </div>
            </div>
          </div>
        ),
        fixed: "left",
        width: 200,
      },
      {
        title: "Department",
        dataIndex: "department",
        key: "department",
        render: (department) => (
          <div style={{ fontWeight: "400", color: "#666" }}>{department || "General"}</div>
        ),
        width: 150,
      },
      {
        title: "Status",
        dataIndex: "statusCode",
        key: "status",
        render: (statusCode, record) => (
          <div className="status-cell">
            <Tag
              color={STATUS_CONFIG[statusCode]?.color}
              className="status-tag"
              style={{
                margin: 0,
                fontWeight: "500",
                border: "none",
              }}
            >
              {getStatusText(statusCode)}
            </Tag>
          </div>
        ),
        width: 120,
      },
      {
        title: "Worked Hours",
        dataIndex: "totalWorkedHours",
        key: "workedHours",
        render: (totalWorkedHours, record) => (
          <div className="hours-cell" style={{ fontWeight: "500", color: "#1890ff" }}>
            {formatWorkedHours(totalWorkedHours || record.hoursWorked)}
          </div>
        ),
        width: 120,
      },
      {
        title: "Action",
        key: "actions",
        render: (_, record) => (
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => handleEmployeeClick(record)}
            className="view-details-btn"
            size="small"
            style={{
              borderRadius: "4px",
              fontWeight: "500",
            }}
          >
            Details
          </Button>
        ),
        width: 100,
        fixed: "right",
      },
    ];

    return (
      <div className="employee-table-container" style={{ marginTop: "0px" }}>
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`,
          }}
          scroll={{ x: 800 }}
          size="middle"
          className="employee-attendance-table"
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
          }}
        />
      </div>
    );
  };

  // Render Work Life Balance tab
  const renderWorkLifeBalance = () => {
    // Generate month options
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: dayjs().month(i).format("MMMM"),
    }));

    // Generate year options (current year and previous 2 years)
    const currentYear = dayjs().year();
    const yearOptions = Array.from({ length: 3 }, (_, i) => ({
      value: currentYear - i,
      label: (currentYear - i).toString(),
    }));
    // Calculate summary statistics using new enum categories
    const totalEmployees = workLifeBalanceData.length;
    const excellentEmployees = workLifeBalanceData.filter((emp) => {
      const balanceStatus = getWorkLifeBalanceStatus(
        emp.differencePercent,
        emp.workedHours,
        emp.expectedHours
      );
      return balanceStatus.statusEnum === WorkLifeBalanceEnum.Excellent;
    }).length;

    const acceptableEmployees = workLifeBalanceData.filter((emp) => {
      const balanceStatus = getWorkLifeBalanceStatus(
        emp.differencePercent,
        emp.workedHours,
        emp.expectedHours
      );
      return balanceStatus.statusEnum === WorkLifeBalanceEnum.Acceptable;
    }).length;

    const poorEmployees = workLifeBalanceData.filter((emp) => {
      const balanceStatus = getWorkLifeBalanceStatus(
        emp.differencePercent,
        emp.workedHours,
        emp.expectedHours
      );
      return balanceStatus.statusEnum === WorkLifeBalanceEnum.Poor;
    }).length;

    const overworkedEmployees = workLifeBalanceData.filter((emp) => {
      const balanceStatus = getWorkLifeBalanceStatus(
        emp.differencePercent,
        emp.workedHours,
        emp.expectedHours
      );
      return balanceStatus.statusEnum === WorkLifeBalanceEnum.Overworked;
    }).length;

    return (
      <div className="work-life-balance-container">
        {/* Filters Section */}
        <Card size="small" style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Department</div>
              <Select
                value={selectedDepartment}
                onChange={setSelectedDepartment}
                style={{ width: 200 }}
                loading={loading.departments}
                placeholder="Select Department"
              >
                {attendanceData.departments.map((dept) => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Month</div>
              <Select value={selectedMonth} onChange={setSelectedMonth} style={{ width: 150 }}>
                {monthOptions.map((month) => (
                  <Option key={month.value} value={month.value}>
                    {month.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Year</div>
              <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 120 }}>
                {yearOptions.map((year) => (
                  <Option key={year.value} value={year.value}>
                    {year.label}
                  </Option>
                ))}
              </Select>
            </div>

            <Button
              className="btn-primary"
              onClick={fetchWorkLifeBalanceData}
              loading={loadingWorkLife}
              style={{ marginTop: "20px" }}
              icon={<SearchOutlined />}
            >
              Load Data
            </Button>

            <Button
              onClick={() => {
                setSelectedMonth(dayjs().month() + 1);
                setSelectedYear(dayjs().year());
              }}
              style={{ marginTop: "20px" }}
            >
              Reset
            </Button>
          </div>
        </Card>

        {/* Loading State */}
        {loadingWorkLife && (
          <div className="text-center py-4">
            <Spin size="large" />
          </div>
        )}

        {/* Empty State */}
        {!loadingWorkLife && (!workLifeBalanceData || workLifeBalanceData.length === 0) && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              selectedDepartment
                ? "No work life balance data available for selected criteria"
                : "Please select a department and click 'Load Data'"
            }
          >
            {!selectedDepartment && (
              <Button
                className="btn-primary"
                onClick={() => {
                  if (attendanceData.departments.length > 0) {
                    setSelectedDepartment(attendanceData.departments[0].id);
                  }
                }}
              >
                Select First Department
              </Button>
            )}
          </Empty>
        )}

        {/* Data Display */}
        {!loadingWorkLife && workLifeBalanceData && workLifeBalanceData.length > 0 && (
          <>
            {/* Summary Statistics */}
            {/* Summary Statistics */}
            <div className="balance-stats" style={{ marginBottom: "16px" }}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Card size="small">
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>{totalEmployees}</div>
                      <div style={{ color: "#666" }}>Total Employees</div>
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                        {excellentEmployees}
                      </div>
                      <div style={{ color: "#666" }}>Excellent</div>
                      <div style={{ fontSize: "12px", color: "#999" }}>Within ±5%</div>
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                        {acceptableEmployees}
                      </div>
                      <div style={{ color: "#666" }}>Acceptable</div>
                      <div style={{ fontSize: "12px", color: "#999" }}>Within ±10%</div>
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                        {poorEmployees + overworkedEmployees}
                      </div>
                      <div style={{ color: "#666" }}>Need Attention</div>
                      <div style={{ fontSize: "12px", color: "#999" }}>
                        Poor: {poorEmployees} | Over: {overworkedEmployees}
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Data Table */}
            <Table
              columns={[
                {
                  title: "Employee",
                  dataIndex: "firstName",
                  key: "name",
                  render: (firstName, record) => (
                    <div
                      className="employee-info-cell"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => fetchEmployeeSummary(record)}
                    >
                      <div className="employee-avatar-small">
                        <UserOutlined style={{ fontSize: "16px", color: "#666" }} />
                      </div>
                      <div className="employee-details">
                        <div className="employee-name" style={{ fontWeight: "500" }}>
                          {firstName} {record.lastName}
                        </div>

                        <div style={{ fontSize: "11px", color: "#ccc", marginTop: "2px" }}>
                          Click for details
                        </div>
                      </div>
                    </div>
                  ),
                  fixed: "left",
                  width: 200,
                },
                {
                  title: "Working Days",
                  dataIndex: "workingDays",
                  key: "workingDays",
                  render: (workingDays, record) => (
                    <div style={{ fontWeight: "500" }}>
                      {workingDays}/{record.totalDays}
                    </div>
                  ),
                  width: 120,
                  sorter: (a, b) => a.workingDays - b.workingDays,
                },
                {
                  title: "Expected Hours",
                  dataIndex: "expectedHours",
                  key: "expectedHours",
                  render: (expectedHours) => (
                    <div style={{ fontWeight: "500", color: "#666" }}>{expectedHours}h</div>
                  ),
                  width: 120,
                  sorter: (a, b) => a.expectedHours - b.expectedHours,
                },
                {
                  title: "Worked Hours",
                  dataIndex: "workedHours",
                  key: "workedHours",
                  render: (workedHours) => <div style={{ fontWeight: "500" }}>{workedHours}h</div>,
                  width: 120,
                  sorter: (a, b) => a.workedHours - b.workedHours,
                },
                {
                  title: "Difference %",
                  dataIndex: "differencePercent",
                  key: "differencePercent",
                  render: (differencePercent) => (
                    <div
                      style={{
                        fontWeight: "500",
                      }}
                    >
                      {differencePercent?.toFixed(2)}%
                    </div>
                  ),
                  width: 120,
                  sorter: (a, b) => a.differencePercent - b.differencePercent,
                },
                {
                  title: "Utilization",
                  key: "utilization",
                  render: (_, record) => {
                    const utilization =
                      record.expectedHours > 0
                        ? (record.workedHours / record.expectedHours) * 100
                        : 0;
                    const balanceStatus = getWorkLifeBalanceStatus(
                      record.differencePercent,
                      record.workedHours,
                      record.expectedHours
                    );

                    return (
                      <div style={{ minWidth: "150px" }}>
                        <Progress
                          percent={Math.round(utilization)}
                          size="small"
                          strokeColor={balanceStatus.color}
                          format={(percent) => `${percent}%`}
                        />
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                          {record.workedHours}h / {record.expectedHours}h
                        </div>
                      </div>
                    );
                  },
                  width: 180,
                  sorter: (a, b) => {
                    const utilA = a.expectedHours > 0 ? (a.workedHours / a.expectedHours) * 100 : 0;
                    const utilB = b.expectedHours > 0 ? (b.workedHours / b.expectedHours) * 100 : 0;
                    return utilA - utilB;
                  },
                },
                {
                  title: "Balance Status",
                  key: "balanceStatus",
                  render: (_, record) => {
                    const balanceStatus = getWorkLifeBalanceStatus(
                      record.differencePercent,
                      record.workedHours,
                      record.expectedHours
                    );

                    return (
                      <Tag
                        color={balanceStatus.color}
                        style={{
                          margin: 0,
                          fontWeight: "500",
                          border: "none",
                          borderRadius: "12px",
                        }}
                      >
                        {balanceStatus.status}
                      </Tag>
                    );
                  },
                  width: 120,
                  filters: [
                    { text: "Excellent", value: WorkLifeBalanceEnum.Excellent },
                    { text: "Acceptable", value: WorkLifeBalanceEnum.Acceptable },
                    { text: "Poor", value: WorkLifeBalanceEnum.Poor },
                    { text: "Overworked", value: WorkLifeBalanceEnum.Overworked },
                  ],
                  onFilter: (value, record) => {
                    const balanceStatus = getWorkLifeBalanceStatus(
                      record.differencePercent,
                      record.workedHours,
                      record.expectedHours
                    );
                    return balanceStatus.statusEnum === value;
                  },
                },
                {
                  title: "Attendance Status",
                  dataIndex: "status",
                  key: "status",
                  render: (status) => (
                    <Tag
                      color={STATUS_CONFIG[status]?.color}
                      style={{
                        margin: 0,
                        fontWeight: "500",
                        border: "none",
                        borderRadius: "12px",
                      }}
                    >
                      {getStatusText(status)}
                    </Tag>
                  ),
                  width: 120,
                  filters: [
                    { text: "Present", value: AttendanceStatusEnum.Present },
                    { text: "Absent", value: AttendanceStatusEnum.Absent },
                    { text: "On Leave", value: AttendanceStatusEnum.OnLeave },
                    { text: "Half Day", value: AttendanceStatusEnum.HalfDay },
                    { text: "Short Time", value: AttendanceStatusEnum.ShortTime },
                  ],
                  onFilter: (value, record) => record.status == value,
                },
              ]}
              dataSource={workLifeBalanceData}
              rowKey="userId"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`,
              }}
              scroll={{ x: 1200 }}
              size="middle"
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: "8px",
              }}
              onChange={(pagination, filters, sorter) => {}}
            />

            {/* Additional Info */}
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                fontSize: "12px",
                color: "#666",
              }}
            >
              <strong>Note:</strong> Work-life balance is calculated based on actual worked hours vs
              expected hours. Utilization below 80% may indicate work-life balance concerns.
            </div>
          </>
        )}
      </div>
    );
  };

  // Render department calendar view
  const renderDepartmentCalendar = () => {
    // If selectedEmployee exists, render individual calendar
    if (selectedEmployee) {
      return (
        <div className="calendar-container">
          <div className="calendar-header">
            <div className="calendar-nav">
              <button onClick={goToPrevious} className="nav-button">
                <i className="fas fa-chevron-left"></i>
              </button>
              <button onClick={goToToday} className="nav-button">
                Today
              </button>
              <button onClick={goToNext} className="nav-button">
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
            <div className="calendar-title">
              {dayjs(currentDate).format("MMMM YYYY")} - {selectedEmployee.name}s Calendar
            </div>
            <div className="calendar-actions">
              <Button onClick={() => setSelectedEmployee(null)} style={{ marginLeft: 8 }}>
                Back to Department
              </Button>
            </div>
          </div>

          {/* Individual Employee Calendar */}
          {renderIndividualEmployeeCalendar()}
        </div>
      );
    }

    // Department calendar view
    const calendarDays = generateCalendarDays();
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <div className="calendar-container">
        {/* Calendar Header */}
        <div className="calendar-header">
          {/* Navigation */}
          <div className="calendar-nav">
            <button onClick={goToPrevious} className="nav-button">
              <i className="fas fa-chevron-left"></i>
            </button>
            <button onClick={goToToday} className="nav-button">
              Today
            </button>
            <button onClick={goToNext} className="nav-button">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          {/* Calendar Title */}
          <div className="calendar-title">
            {dayjs(currentDate).format("MMMM YYYY")} - Department Attendance
          </div>

          {/* View Options and Filters */}
          <div className="calendar-actions">
            <Select
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              style={{ width: 200 }}
              className="department-select"
              loading={loading.departments}
              placeholder="Select Department"
            >
              {attendanceData.departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Select Employee"
              value={selectedEmployee?.id || null}
              onChange={handleEmployeeSelect}
              style={{ width: 200, marginLeft: 8 }}
              loading={loading.employees}
              allowClear
            >
              <Option value={null}>All Employees</Option>
              {attendanceData.employees.map((employee) => (
                <Option key={employee.id} value={employee.id}>
                  {employee.name}
                </Option>
              ))}
            </Select>

            <Select
              value={calendarView}
              onChange={setCalendarView}
              style={{ width: 120, marginLeft: 8 }}
            >
              <Option value="month">Month</Option>
              <Option value="week">Week</Option>
            </Select>
          </div>
        </div>

        {/* Week Days Header */}
        <div className="calendar-weekdays">
          {weekDays.map((day) => (
            <div key={day} className="calendar-weekday">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className={`calendar-grid ${calendarView}-view`}>
          {calendarDays.map(({ date, isOtherMonth }) => {
            const attendance = getDepartmentAttendanceForDate(date);
            const isToday = dayjs(date).isSame(dayjs(), "day");
            const isFuture = dayjs(date).isAfter(dayjs(), "day");
            const isWeekend = attendance?.isWeekend;
            const isHoliday = attendance?.isHoliday;
            const isHovered = hoveredDate === date.toISOString();

            // Determine status based on department data using enums
            let status = null;
            let statusText = "";

            if (isWeekend) {
              status = STATUS_CONFIG[AttendanceStatusEnum.Weekend];
              statusText = AttendenceStatusLabel[AttendanceStatusEnum.Weekend];
            } else if (isHoliday) {
              status = STATUS_CONFIG[AttendanceStatusEnum.Holiday];
              statusText = AttendenceStatusLabel[AttendanceStatusEnum.Holiday];
            } else if (attendance && attendance.users && attendance.users.length > 0) {
              // Determine department status based on majority using enums
              const presentCount = attendance.totalPresent || 0;
              const absentCount = attendance.totalAbsent || 0;
              const leaveCount = attendance.totalOnLeave || 0;

              if (presentCount > 0) {
                status = STATUS_CONFIG[AttendanceStatusEnum.Present];
                statusText = AttendenceStatusLabel[AttendanceStatusEnum.Present];
              } else if (absentCount > 0) {
                status = STATUS_CONFIG[AttendanceStatusEnum.Absent];
                statusText = AttendenceStatusLabel[AttendanceStatusEnum.Absent];
              } else if (leaveCount > 0) {
                status = STATUS_CONFIG[AttendanceStatusEnum.OnLeave];
                statusText = AttendenceStatusLabel[AttendanceStatusEnum.OnLeave];
              }
            }

            return (
              <div
                key={date.toISOString()}
                className={`calendar-day ${isOtherMonth ? "other-month" : ""} ${
                  isToday ? "today" : ""
                } ${isFuture ? "future-date" : ""} ${isWeekend ? "weekend" : ""} ${
                  isHoliday ? "holiday" : ""
                }`}
                onMouseEnter={() => !isFuture && setHoveredDate(date.toISOString())}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {/* Future Date Overlay */}
                {isFuture && (
                  <div className="future-date-overlay">
                    <i className="fas fa-lock"></i>
                    <span>Future Date</span>
                  </div>
                )}

                {/* Day Header */}
                <div className="day-header">
                  <div className={`day-number ${isToday ? "today" : ""}`}>{date.getDate()}</div>
                  {status && !isOtherMonth && !isFuture && (
                    <div
                      className={`day-status ${status.class}`}
                      style={{
                        backgroundColor: status.backgroundColor,
                        color: status.color,
                        border: `1px solid rgba(${hexToRgb(status.color)}, 0.2)`,
                      }}
                    >
                      {status.short}
                    </div>
                  )}
                </div>

                {/* Day Content - Department Summary */}
                {!isOtherMonth &&
                  !isFuture &&
                  attendance &&
                  attendance.users &&
                  attendance.users.length > 0 && (
                    <div className="day-content">
                      {/* Present Employees */}
                      {attendance.totalPresent > 0 && (
                        <div className="day-hours">
                          <i
                            className="fas fa-check-circle"
                            style={{ color: STATUS_CONFIG[AttendanceStatusEnum.Present].color }}
                          ></i>
                          {attendance.totalPresent}{" "}
                          {AttendenceStatusLabel[AttendanceStatusEnum.Present]}
                        </div>
                      )}

                      {/* Absent Employees */}
                      {attendance.totalAbsent > 0 && (
                        <div className="day-overtime">
                          <i
                            className="fas fa-times-circle"
                            style={{ color: STATUS_CONFIG[AttendanceStatusEnum.Absent].color }}
                          ></i>
                          {attendance.totalAbsent}{" "}
                          {AttendenceStatusLabel[AttendanceStatusEnum.Absent]}
                        </div>
                      )}

                      {/* On Leave */}
                      {attendance.totalOnLeave > 0 && (
                        <div className="day-indicators">
                          <div
                            className="indicator"
                            style={{
                              background:
                                STATUS_CONFIG[AttendanceStatusEnum.OnLeave].backgroundColor,
                              color: STATUS_CONFIG[AttendanceStatusEnum.OnLeave].color,
                            }}
                          >
                            <i className="fas fa-umbrella-beach"></i>
                            {attendance.totalOnLeave}{" "}
                            {AttendenceStatusLabel[AttendanceStatusEnum.OnLeave]}
                          </div>
                        </div>
                      )}

                      {/* Late Arrivals */}
                      {attendance.totalLate > 0 && (
                        <div className="day-indicators">
                          <div className="indicator indicator-late">
                            <i className="fas fa-clock"></i>
                            {attendance.totalLate} Late
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {/* Special States */}
                {!isOtherMonth && !isFuture && isWeekend && (
                  <div className="day-content">
                    <div className="day-hours">
                      <i
                        className="fas fa-calendar-weekend"
                        style={{ color: STATUS_CONFIG[AttendanceStatusEnum.Weekend].color }}
                      ></i>
                      {AttendenceStatusLabel[AttendanceStatusEnum.Weekend]}
                    </div>
                  </div>
                )}

                {!isOtherMonth && !isFuture && isHoliday && (
                  <div className="day-content">
                    <div className="day-hours">
                      <i
                        className="fas fa-party-horn"
                        style={{ color: STATUS_CONFIG[AttendanceStatusEnum.Holiday].color }}
                      ></i>
                      {AttendenceStatusLabel[AttendanceStatusEnum.Holiday]}
                    </div>
                  </div>
                )}

                {/* No Data */}
                {!isOtherMonth &&
                  !isFuture &&
                  !isWeekend &&
                  !isHoliday &&
                  (!attendance || !attendance.users || attendance.users.length === 0) && (
                    <div className="day-content">
                      <div className="day-hours">
                        <i className="fas fa-database"></i>
                        No Data
                      </div>
                    </div>
                  )}

                {/* Hover Details */}
                {isHovered && !isFuture && (
                  <div className="day-details">
                    <h6>{dayjs(date).format("ddd, MMM D, YYYY")}</h6>

                    {isWeekend ? (
                      <p>
                        <strong>Status:</strong>{" "}
                        {AttendenceStatusLabel[AttendanceStatusEnum.Weekend]} - No attendance
                        tracking
                      </p>
                    ) : isHoliday ? (
                      <p>
                        <strong>Status:</strong>{" "}
                        {AttendenceStatusLabel[AttendanceStatusEnum.Holiday]} - No attendance
                        tracking
                      </p>
                    ) : attendance && attendance.users && attendance.users.length > 0 ? (
                      <>
                        <p>
                          <strong>Department Summary</strong>
                        </p>
                        <p style={{ color: STATUS_CONFIG[AttendanceStatusEnum.Present].color }}>
                          {STATUS_CONFIG[AttendanceStatusEnum.Present].icon}{" "}
                          {AttendenceStatusLabel[AttendanceStatusEnum.Present]}:{" "}
                          {attendance.totalPresent || 0}
                        </p>
                        <p style={{ color: STATUS_CONFIG[AttendanceStatusEnum.Absent].color }}>
                          {STATUS_CONFIG[AttendanceStatusEnum.Absent].icon}{" "}
                          {AttendenceStatusLabel[AttendanceStatusEnum.Absent]}:{" "}
                          {attendance.totalAbsent || 0}
                        </p>
                        <p style={{ color: STATUS_CONFIG[AttendanceStatusEnum.OnLeave].color }}>
                          {STATUS_CONFIG[AttendanceStatusEnum.OnLeave].icon}{" "}
                          {AttendenceStatusLabel[AttendanceStatusEnum.OnLeave]}:{" "}
                          {attendance.totalOnLeave || 0}
                        </p>
                        {attendance.totalLate > 0 && (
                          <p style={{ color: "#f59e0b" }}>
                            <i className="fas fa-clock"></i> Late Arrivals: {attendance.totalLate}
                          </p>
                        )}
                        <p>
                          <strong>Total Employees:</strong> {attendance.users.length}
                        </p>
                      </>
                    ) : (
                      <p>
                        <strong>Status:</strong> No attendance data available
                      </p>
                    )}

                    <div className="click-hint">Hover for details</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Status Legend */}
        <div className="status-legend">
          <div className="legend-title">Department Status Legend:</div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: "#4ade80" }}></div>
              <span>Present</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: "#ef4444" }}></div>
              <span>Absent</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: "#8b5cf6" }}></div>
              <span>On Leave</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: "#f59e0b" }}></div>
              <span>Late Arrivals</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: "#94a3b8" }}></div>
              <span>Weekend</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: "#f43f5e" }}></div>
              <span>Holiday</span>
            </div>
          </div>
        </div>

        {/* Add Font Awesome CSS */}
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
        />
      </div>
    );
  };

  // Safe access to summary data
  const summary = attendanceData.summary || {
    totalEmployees: 0,
    presentToday: 0,
    onLeave: 0,
    lateArrivals: 0,
    absentToday: 0,
  };

  // Employee Summary Modal
  const renderEmployeeSummaryModal = () => (
    <Modal
      title={
        <div>
          <UserOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
          Employee Work-Life Balance Summary
        </div>
      }
      open={employeeSummaryModalVisible}
      onCancel={() => setEmployeeSummaryModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setEmployeeSummaryModalVisible(false)}>
          Close
        </Button>,
        <Button
          key="download"
          className="btn-primary"
          icon={<DownloadOutlined />}
          onClick={() => message.success("Employee report exported!")}
        >
          Export Report
        </Button>,
      ]}
      width={800}
      centered
    >
      <Spin spinning={loadingEmployeeSummary}>
        {employeeSummaryData && selectedEmployeeForSummary && (
          <div className="employee-summary-content">
            {/* Employee Header */}
            <div
              className="employee-header"
              style={{
                padding: "16px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div className="employee-avatar-large">
                  <UserOutlined style={{ fontSize: "24px", color: "#666" }} />
                </div>
                <div>
                  <h4 style={{ margin: 0, color: "#1890ff" }}>
                    {selectedEmployeeForSummary.firstName} {selectedEmployeeForSummary.lastName}
                  </h4>
                  <p style={{ margin: "4px 0 0 0", color: "#666" }}>
                    Employee ID: {selectedEmployeeForSummary.userId} | Period:{" "}
                    {dayjs()
                      .month(selectedMonth - 1)
                      .format("MMMM")}{" "}
                    {selectedYear}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1890ff" }}>
                      {employeeSummaryData.totalDays || selectedEmployeeForSummary.totalDays}
                    </div>
                    <div style={{ color: "#666", fontSize: "12px" }}>Total Days</div>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#4CAF50" }}>
                      {employeeSummaryData.workingDays || selectedEmployeeForSummary.workingDays}
                    </div>
                    <div style={{ color: "#666", fontSize: "12px" }}>Working Days</div>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#FFC107" }}>
                      {employeeSummaryData.expectedHours ||
                        selectedEmployeeForSummary.expectedHours}
                      h
                    </div>
                    <div style={{ color: "#666", fontSize: "12px" }}>Expected Hours</div>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1890ff" }}>
                      {employeeSummaryData.workedHours || selectedEmployeeForSummary.workedHours}h
                    </div>
                    <div style={{ color: "#666", fontSize: "12px" }}>Worked Hours</div>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Detailed Information */}
            <Card title="Detailed Analysis" size="small">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <h5 style={{ marginBottom: "8px" }}>Attendance Status</h5>
                  <Tag
                    color={STATUS_CONFIG[selectedEmployeeForSummary.status]?.color}
                    style={{
                      fontSize: "14px",
                      padding: "4px 8px",
                      borderRadius: "12px",
                    }}
                  >
                    {getStatusText(selectedEmployeeForSummary.status)}
                  </Tag>
                </div>

                <div>
                  <h5 style={{ marginBottom: "8px" }}>Utilization Rate</h5>
                  <Progress
                    percent={Math.round(
                      ((employeeSummaryData?.workedHours ||
                        selectedEmployeeForSummary.workedHours) /
                        (employeeSummaryData?.expectedHours ||
                          selectedEmployeeForSummary.expectedHours)) *
                        100
                    )}
                    size="small"
                    strokeColor={
                      getWorkLifeBalanceStatus(
                        selectedEmployeeForSummary.differencePercent,
                        selectedEmployeeForSummary.workedHours,
                        selectedEmployeeForSummary.expectedHours
                      ).color
                    }
                  />
                </div>

                <div>
                  <h5 style={{ marginBottom: "8px" }}>Difference</h5>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color:
                        selectedEmployeeForSummary.differencePercent >= 0 ? "#4CAF50" : "#F44336",
                    }}
                  >
                    {selectedEmployeeForSummary.differencePercent?.toFixed(2)}%
                  </div>
                </div>

                <div>
                  <h5 style={{ marginBottom: "8px" }}>Balance Status</h5>
                  <Tag
                    color={
                      getWorkLifeBalanceStatus(
                        selectedEmployeeForSummary.differencePercent,
                        selectedEmployeeForSummary.workedHours,
                        selectedEmployeeForSummary.expectedHours
                      ).color
                    }
                    style={{
                      fontSize: "14px",
                      padding: "4px 8px",
                      borderRadius: "12px",
                    }}
                  >
                    {
                      getWorkLifeBalanceStatus(
                        selectedEmployeeForSummary.differencePercent,
                        selectedEmployeeForSummary.workedHours,
                        selectedEmployeeForSummary.expectedHours
                      ).status
                    }
                  </Tag>
                </div>
              </div>

              {/* Additional Notes */}
              {employeeSummaryData?.notes && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    backgroundColor: "#fff3cd",
                    borderRadius: "4px",
                  }}
                >
                  <h5 style={{ margin: "0 0 8px 0", color: "#856404" }}>Notes</h5>
                  <p style={{ margin: 0, color: "#856404", fontSize: "14px" }}>
                    {employeeSummaryData.notes}
                  </p>
                </div>
              )}
            </Card>

            {/* Performance Insights */}
            <Card title="Performance Insights" size="small" style={{ marginTop: "16px" }}>
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                <li>
                  <strong>Working Days Ratio:</strong> {selectedEmployeeForSummary.workingDays}/
                  {selectedEmployeeForSummary.totalDays}(
                  {Math.round(
                    (selectedEmployeeForSummary.workingDays /
                      selectedEmployeeForSummary.totalDays) *
                      100
                  )}
                  %)
                </li>
                <li>
                  <strong>Hours Utilization:</strong> {selectedEmployeeForSummary.workedHours}h /{" "}
                  {selectedEmployeeForSummary.expectedHours}h
                </li>
                <li>
                  <strong>Performance:</strong>{" "}
                  {selectedEmployeeForSummary.differencePercent >= -10
                    ? "Good"
                    : selectedEmployeeForSummary.differencePercent >= -30
                    ? "Moderate"
                    : "Needs Improvement"}
                </li>
              </ul>
            </Card>
          </div>
        )}

        {!employeeSummaryData && !loadingEmployeeSummary && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No detailed data available for this employee"
          />
        )}
      </Spin>
    </Modal>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="hr-dashboard-container">
        {/* Header */}
        <div className="attendance-header-card">
          <div>
            <h2 style={{ color: "var(--text-main)" }}>HR Attendance Dashboard</h2>
          </div>
        </div>

        {/* Admin Timing Configuration */}
        {/* Admin Timing Configuration */}
        <Card
          size="small"
          style={{
            marginBottom: "16px",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderLeft: "4px solid #599c16", // Brand green accent
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
          loading={loadingTimings}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: isEditingTimings ? "20px" : "0",
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#0c313f",
                  fontWeight: "600",
                }}
              >
                <i className="fas fa-clock" style={{ color: "#599c16" }}></i>
                Work Timing Configuration
              </h4>
              <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "12px" }}>
                Configure working hours, grace period, and attendance thresholds
              </p>
            </div>
            <Button
              className={isEditingTimings ? "" : "btn-primary"}
              size="small"
              onClick={() => setIsEditingTimings(!isEditingTimings)}
              style={{
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              icon={
                isEditingTimings ? (
                  <i className="fas fa-times"></i>
                ) : (
                  <i className="fas fa-edit"></i>
                )
              }
            >
              {isEditingTimings ? "Cancel" : "Configure Timings"}
            </Button>
          </div>

          {isEditingTimings ? (
            <div style={{ marginTop: "8px" }}>
              <Row gutter={[16, 16]}>
                {[
                  { label: "Start Time", key: "startTime", type: "time", span: 6 },
                  { label: "End Time", key: "endTime", type: "time", span: 6 },
                  {
                    label: "Grace Minutes",
                    key: "graceMinutes",
                    type: "number",
                    span: 4,
                    addon: "mins",
                  },
                  {
                    label: "Full Day Hours",
                    key: "fullDayRequiredHours",
                    type: "number",
                    span: 4,
                    addon: "hours",
                  },
                  {
                    label: "Half Day Hours",
                    key: "halfDayThresholdHours",
                    type: "number",
                    span: 4,
                    addon: "hours",
                  },
                ].map((field) => (
                  <Col xs={24} sm={12} md={field.span} key={field.key}>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#0c313f",
                        marginBottom: "4px",
                        fontWeight: "600",
                      }}
                    >
                      {field.label}{" "}
                      {field.type === "time" && <span style={{ color: "#ff4d4f" }}>*</span>}
                    </div>
                    <Input
                      type={field.type}
                      value={workTimingSettings[field.key]}
                      addonAfter={field.addon}
                      onChange={(e) =>
                        setWorkTimingSettings((prev) => ({
                          ...prev,
                          [field.key]:
                            field.type === "number"
                              ? parseInt(e.target.value) || 0
                              : e.target.value,
                        }))
                      }
                      style={{ width: "100%", borderRadius: "6px" }}
                      step={field.type === "time" ? "300" : undefined}
                    />
                  </Col>
                ))}
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                <Col span={24}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#0c313f",
                      marginBottom: "4px",
                      fontWeight: "600",
                    }}
                  >
                    Effective Date <span style={{ color: "#ff4d4f" }}>*</span>
                  </div>
                  <DatePicker
                    value={
                      workTimingSettings.effectiveDate
                        ? dayjs(workTimingSettings.effectiveDate)
                        : null
                    }
                    onChange={(date) =>
                      setWorkTimingSettings((prev) => ({
                        ...prev,
                        effectiveDate: date
                          ? date.format("YYYY-MM-DD")
                          : dayjs().format("YYYY-MM-DD"),
                      }))
                    }
                    style={{ width: "200px", borderRadius: "6px" }}
                    format="YYYY-MM-DD"
                  />
                </Col>
              </Row>

              <div style={{ marginTop: "20px", display: "flex", gap: "8px" }}>
                <Button
                  className="btn-primary"
                  onClick={saveWorkTimingSettings}
                  loading={loadingTimings}
                  icon={<i className="fas fa-save"></i>}
                  style={{ borderRadius: "6px" }}
                >
                  Save Settings
                </Button>
                <Button
                  onClick={() => setIsEditingTimings(false)}
                  disabled={loadingTimings}
                  style={{ borderRadius: "6px" }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: "16px" }}>
              <Row gutter={[12, 12]}>
                {[
                  {
                    label: "Start Time",
                    value:
                      workTimingSettings.startTimeAMPM ||
                      formatToAMPM(workTimingSettings.startTime),
                  },
                  {
                    label: "End Time",
                    value:
                      workTimingSettings.endTimeAMPM || formatToAMPM(workTimingSettings.endTime),
                  },
                  { label: "Grace Period", value: `${workTimingSettings.graceMinutes || 15} mins` },
                  { label: "Full Day", value: `${workTimingSettings.fullDayRequiredHours || 8}h` },
                  { label: "Half Day", value: `${workTimingSettings.halfDayThresholdHours || 4}h` },
                  {
                    label: "Effective From",
                    value: workTimingSettings.effectiveDate
                      ? dayjs(workTimingSettings.effectiveDate).format("MMM D, YYYY")
                      : dayjs().format("MMM D, YYYY"),
                    highlight: true,
                  },
                ].map((item, idx) => (
                  <Col xs={12} sm={6} md={4} key={idx}>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "12px 8px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#64748b",
                          textTransform: "uppercase",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          marginBottom: "4px",
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        style={{
                          fontSize: item.highlight ? "13px" : "15px",
                          fontWeight: "700",
                          color: "#0c313f",
                        }}
                      >
                        {item.value}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card>

        {/* Quick Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }} className="stats-row">
          {/* Total Employees */}
          <Col xs={12} sm={6}>
            <div className="stats-card-container theme-employees">
              <div className="stats-icon-wrapper">
                <TeamOutlined />
              </div>
              <div className="stats-info-content">
                <span className="stats-label-text">Total Employees</span>
                <span className="stats-value-text">{summary.totalEmployees}</span>
              </div>
            </div>
          </Col>

          {/* Present Today */}
          <Col xs={12} sm={6}>
            <div className="stats-card-container theme-present">
              <div className="stats-icon-wrapper">
                <UserOutlined />
              </div>
              <div className="stats-info-content">
                <span className="stats-label-text">Present Today</span>
                <span className="stats-value-text">{summary.presentToday}</span>
              </div>
            </div>
          </Col>

          {/* On Leave */}
          <Col xs={12} sm={6}>
            <div className="stats-card-container theme-leave">
              <div className="stats-icon-wrapper">
                <i className="fas fa-umbrella-beach"></i>
              </div>
              <div className="stats-info-content">
                <span className="stats-label-text">On Leave</span>
                <span className="stats-value-text">{summary.onLeave}</span>
              </div>
            </div>
          </Col>

          {/* Late Arrivals */}
          <Col xs={12} sm={6}>
            <div className="stats-card-container theme-late">
              <div className="stats-icon-wrapper">
                <ClockCircleOutlined />
              </div>
              <div className="stats-info-content">
                <span className="stats-label-text">Late Arrivals</span>
                <span className="stats-value-text">{summary.lateArrivals}</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Content Tabs */}
        <Card className="card-professional">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="custom-tabs"
            type="card"
            tabBarGutter={8}
            items={[
              {
                key: "today",
                label: (
                  <span>
                    <CalendarOutlined />
                    Todays Attendance
                    <Badge count={getTodayEmployees().length} offset={[10, -5]} />
                  </span>
                ),
                children: (
                  <div className="tab-content" style={{ padding: "0px" }}>
                    <div
                      className="tab-header"
                      style={{
                        padding: "16px 0px",
                        marginBottom: "0px",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <h5 style={{ margin: "0 0 16px 0" }}>
                        Employee Attendance - {dayjs().format("MMMM D, YYYY")}
                      </h5>

                      <div
                        className="tab-filters"
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          marginBottom: "0px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Input
                          placeholder="Search employees..."
                          prefix={<SearchOutlined />}
                          style={{ width: 200 }}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          allowClear
                        />
                        <Select
                          placeholder="All Departments"
                          style={{ width: 200 }}
                          value={departmentFilter || undefined}
                          onChange={(value) => {
                            setDepartmentFilter(value);
                            // If a department is selected, refresh the data
                            if (value) {
                              let startDate, endDate;

                              if (viewTypeFilter === 0) {
                                // Week view
                                const today = dayjs();
                                startDate = today.startOf("week").format("YYYY-MM-DD");
                                endDate = today.endOf("week").format("YYYY-MM-DD");
                              } else {
                                // Month view
                                const today = dayjs();
                                startDate = today.startOf("month").format("YYYY-MM-DD");
                                endDate = today.endOf("month").format("YYYY-MM-DD");
                              }

                              // Update the selected department in the hook as well
                              setSelectedDepartment(value);

                              // Load the data
                              loadDepartmentAttendance(value, viewTypeFilter, startDate, endDate);
                            } else {
                              // If department is cleared, clear the data
                              setSelectedDepartment(null);
                            }
                          }}
                          allowClear
                          loading={loading.departments}
                        >
                          {attendanceData.departments.map((dept) => (
                            <Option key={dept.id} value={dept.id}>
                              {dept.name}
                            </Option>
                          ))}
                        </Select>
                        <Select
                          placeholder="All Status"
                          style={{ width: 200 }}
                          value={statusFilter}
                          onChange={setStatusFilter}
                          allowClear
                        >
                          {Object.entries(AttendenceStatusLabel).map(([code, label]) => (
                            <Option key={code} value={code}>
                              {label}
                            </Option>
                          ))}
                        </Select>
                        <Select
                          placeholder="View Type"
                          style={{ width: 150 }}
                          value={viewTypeFilter}
                          onChange={setViewTypeFilter}
                        >
                          <Option value={0}>Week View</Option>
                          <Option value={1}>Month View</Option>
                        </Select>
                        <Button
                          className="btn-primary"
                          icon={<SearchOutlined />}
                          onClick={refreshTodayData}
                          loading={loading.department}
                        >
                          Refresh
                        </Button>
                      </div>
                    </div>
                    {renderEmployeeTable()}
                  </div>
                ),
              },
              {
                key: "calendar",
                label: (
                  <span>
                    <CalendarOutlined />
                    Department Calendar
                  </span>
                ),
                children: (
                  <div className="tab-content">
                    <div className="tab-header">
                      <h5>
                        Department Attendance Calendar - {dayjs(currentDate).format("MMMM YYYY")}
                      </h5>
                    </div>
                    {renderDepartmentCalendar()}
                  </div>
                ),
              },
              {
                key: "worklife",
                label: (
                  <span>
                    <DashboardOutlined />
                    Work Life Balance
                  </span>
                ),
                children: (
                  <div className="tab-content">
                    <div className="tab-header">
                      <h5>
                        Employee Work-Life Balance Analysis -{" "}
                        {dayjs(currentDate).format("MMMM YYYY")}
                      </h5>
                      <p style={{ color: "#666", margin: "0" }}>
                        Monitor employee workload and work-life balance based on working hours
                        utilization
                      </p>
                    </div>
                    {renderWorkLifeBalance()}
                  </div>
                ),
              },
            ]}
          />
        </Card>

        {/* Add Employee Summary Modal */}
        {renderEmployeeSummaryModal()}
      </div>
    </DashboardLayout>
  );
};

export default DepAttendance;
